/**
 * Sanitization utilities for preventing XSS attacks
 */

/**
 * Sanitizes text content by escaping HTML special characters
 * Prevents XSS attacks when displaying user-generated content
 * 
 * @param text - The text to sanitize
 * @returns Sanitized text safe for display
 * 
 * @example
 * const userEmail = sanitizeText(unsafeEmail);
 * return <div>{userEmail}</div>;
 */
export function sanitizeText(text: string): string {
  const element = document.createElement('div');
  element.textContent = text;
  return element.innerHTML;
}

/**
 * Sanitizes HTML content by allowing only safe tags
 * Use sparingly - prefer sanitizeText for most cases
 * 
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML with only allowed tags
 */
export function sanitizeHtml(html: string): string {
  const allowedTags = ['b', 'i', 'em', 'strong', 'p', 'br'];
  const div = document.createElement('div');
  div.innerHTML = html;
  
  // Remove all script tags and event handlers
  const scripts = div.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Remove event handler attributes
  const allElements = div.querySelectorAll('*');
  allElements.forEach(element => {
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('on')) {
        element.removeAttribute(attr.name);
      }
    });
    
    // Remove elements not in allowed list
    if (!allowedTags.includes(element.tagName.toLowerCase())) {
      element.replaceWith(...element.childNodes);
    }
  });
  
  return div.innerHTML;
}

/**
 * Validates and sanitizes a URL to prevent javascript: and data: schemes
 * 
 * @param url - The URL to validate
 * @returns Sanitized URL or empty string if invalid
 */
export function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    // Only allow http, https, and mailto protocols
    if (['http:', 'https:', 'mailto:'].includes(parsed.protocol)) {
      return parsed.href;
    }
  } catch {
    // Invalid URL
  }
  return '';
}
